import React from 'react';
import { NumberInput } from 'ui-toolkit';
import { updateEscrowComputedFields } from '../utilities/commonComputations';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import Currency from '../Currency';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IHasMetaDataModalProps from './IHasMetaDataModalProps';
import IHasMetaDataModalState from './IHasMetaDataModalState';
import IItemProps from './IItemProps';
import ItemMetaDataModal, { IItemMetaDataModalRef } from './ItemMetaDataModal/ItemMetaDataModal';
import SharedItemCells from './SharedItemCells';
import './_EscrowAtClosing.scss';
import { IEscrowAtClosingViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { eEscrowPrepaymentsAtClosing } from '../../../ui-toolkit/enums/eEscrowPrepaymentsAtClosing';
import { isNil } from 'lodash';

const labelForSubType = {
  [eEscrowPrepaymentsAtClosing.homeowners_insurance]: "Homeowner's Insurance Premium",
  [eEscrowPrepaymentsAtClosing.mortgage_insurance]: 'Mortgage Insurance Premium',
  [eEscrowPrepaymentsAtClosing.property_tax]: 'Property Taxes',
};

interface IEscrowAtClosingProps
  extends IItemProps,
    IHasItemDataProps<IEscrowAtClosingViewItem>,
    Required<IHasItemEventProps<IEscrowAtClosingViewItem>>,
    IHasMetaDataModalProps {}

type IEscrowAtClosingState = IHasMetaDataModalState;

export default class EscrowAtClosing extends React.Component<IEscrowAtClosingProps, IEscrowAtClosingState> {
  public state = { expanded: !this.props.disableAutoExpand && this.props.data.new };
  private modal = React.createRef<IItemMetaDataModalRef>();
  private firstInputNode: HTMLInputElement | null = null;

  public render() {
    const { data, onChange, hasEditPermissions } = this.props;
    const monthlyPayment = data.monthly_payment;
    const generateComputedOnChangeHandler = createOnChangeHandlerGenerator(data, (item: IEscrowAtClosingViewItem) =>
      onChange(updateEscrowComputedFields(item, 'buyer_at_closing'))
    );
    const monthlyPaymentOnChangeEvent = generateComputedOnChangeHandler('monthly_payment');
    const monthsOnChangeEvent = generateComputedOnChangeHandler('months');
    return (
      <div className="escrow-at-closing">
        <SharedItemCells {...this.props} readOnly={!hasEditPermissions}>
          <ItemMetaDataModal
            label={
              <span>
                {labelForSubType[data.sub_type]} (
                <strong>
                  {isNil(monthlyPayment) ? '__' : <Currency value={monthlyPayment} />} /mon for{' '}
                  {isNil(data.months) ? '__' : data.months} mon
                </strong>
                )
              </span>
            }
            expanded={this.state.expanded}
            expandedLabel={labelForSubType[data.sub_type]}
            onToggle={this.onModalToggleEvent}
            ref={this.modal}
          >
            <div className="escrow-at-closing__extra">
              <div className="escrow-at-closing__payment">
                <NumberInput
                  label="Rate per month"
                  mode="currency"
                  name="monthly_payment"
                  getInputRef={this.setFirstInputNode}
                  onBlur={this.onBlurEvent}
                  onChange={monthlyPaymentOnChangeEvent}
                  value={data.monthly_payment}
                  readOnly={!hasEditPermissions}
                  debounceOnValueChange
                />
              </div>
              <div className="escrow-at-closing__months">
                <NumberInput
                  label="No. of months"
                  allowNegative={false}
                  name="months"
                  onBlur={this.onBlurEvent}
                  onChange={monthsOnChangeEvent}
                  value={data.months}
                  readOnly={!hasEditPermissions}
                  debounceOnValueChange
                />
              </div>
            </div>
          </ItemMetaDataModal>
        </SharedItemCells>
      </div>
    );
  }

  private onBlurEvent = (event: React.FocusEvent) => {
    const node = this.modal.current;
    if (node && event.relatedTarget && !node.modalContains(event.relatedTarget as HTMLElement)) {
      this.setState({
        expanded: false,
      });
    }
  };

  private onModalToggleEvent = (expanded: boolean) => {
    this.setState({ expanded });
    if (this.firstInputNode) {
      this.firstInputNode.focus();
    }
  };

  private setFirstInputNode = (input: HTMLInputElement | null) => {
    this.firstInputNode = input;
  };
}
